import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from 'react';
import {
  ThreadPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  ActionBarPrimitive,
  BranchPickerPrimitive,
  AttachmentPrimitive,
  AuiIf,
  useAui,
  useAuiState,
  type TextMessagePartComponent,
} from '@assistant-ui/react';
import { MarkdownTextPrimitive } from '@assistant-ui/react-markdown';
import remarkGfm from 'remark-gfm';
import { useArtifactQuickActions } from '@/lib/artifact-ask';
import {
  attachmentImageBox,
  formatAttachmentBytes,
  useAgentAttachmentLink,
} from '@/lib/agent-attachments';
import { NovaAvatar } from '../../components/common/NovaAvatar';
import { NovaSparkle } from '../../components/common/NovaSparkle';
import { MaterialSymbol } from '../../components/icons/MaterialSymbol';
import {
  CVIcon,
  InterviewIcon,
  JobIcon,
  TailorCVIcon,
} from '../../components/icons/SuggestionIcons.web';
import { RouteContextIntro } from './RouteContextIntro.web';
import { getEmployerRouteIntroKey } from './route-context-intros.employer';
import { getRouteIntro, getScreenTag } from './route-context-intros';
import { derivePageContextScreen } from '../../lib/page-context';
import { FileImage, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { colors } from '@tabbio/tokens';
import {
  markInitialPromptSent,
  normalizeInitialPrompt,
  shouldSendInitialPrompt,
} from '@/lib/chat-initial-prompt';
import { getWebWorkspaceClassName } from '@/lib/chat-navigation';
import { useArtifactPanel } from '../artifacts/artifact-panel-store';
import { useShownArtifactPanel } from '../layout/workspace-transition';
import { useRouter, usePathname } from 'expo-router';
import { ToolCardRenderer, HIDDEN_TOOLS } from './tool-cards';
import { ActivityTrace } from './activity-trace.web';
import { settleStage, type TracePart } from './activity-trace-model';
import { useRunTiming } from './agent-run-timing';
import { deriveSources } from './streaming-sources';
import {
  FollowUpList,
  MarkdownLinkChip,
  SourcesList,
  SourcesToggle,
  STREAMING_REHYPE_PLUGINS,
} from './streaming-text.web';
import type { AIActionKey } from '@tabbio/types';
import { useAiActionGuard, useSubscriptionState } from '../../providers/useSubscriptionState';
import { useUpgradeModal } from '../../providers/UpgradeModalProvider';
import { useAuth } from '../../hooks/useAuth';
import { useWorkspace } from '../../providers/useWorkspace';
import { useToast } from '../../hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../lib/query-keys';
import {
  setMessageFeedback,
  clearMessageFeedback,
  type MessageFeedbackValue,
} from '../../lib/agent-message-feedback-api';
import { captureSentryException } from '../../lib/sentry';
import { selectAiCreditLimitBanner } from '../../lib/ai-credit-limit-banner';
import { createChatUpgradeContext } from '../../lib/monetization-context';
import { ComposerBar } from './composer-bar.web';
import {
  getFileExtension,
  getFileTypeInfo,
  useAttachmentSrc,
} from './composer-attachments.web';
import { runIfComposerAvailable, runIfMessageEditAvailable } from './composer-errors';
import { useAgentMessageDisplayText } from './chat-mode-context';
import { useWebKeyboardInset } from '../../hooks/useWebKeyboardInset';
import { useWebDrawer } from '../layout/WebDrawerContext';
import { MobileWebHome } from './MobileWebHome.web';


// ---------------------------------------------------------------------------
// User Message Attachment Components
// ---------------------------------------------------------------------------
/** The block a sent image sits in (the composer preview's style): 280 by 240 at most. */
const MESSAGE_IMAGE_BLOCK = { width: 280, height: 240 } as const;

/**
 * The composer's page tag ("My CVs", "This job"): the screen the agent will
 * act on. Hidden from the UI on every screen (owner request, 2026-10-04) but
 * kept, with its page-context envelope unchanged, because it is coming back.
 */
const SHOW_COMPOSER_SCREEN_TAG = false;

/**
 * A sent image: the local preview while the message is new, then the
 * history's signed link (read again when it expires or fails), in a box
 * sized from the image's own width and height so nothing jumps.
 */
const MessageImageAttachment: FC = () => {
  const { t } = useTranslation('common');
  const name = useAuiState((s) => s.attachment.name);
  const id = useAuiState((s) => s.attachment.id);
  const localSrc = useAttachmentSrc();
  const link = useAgentAttachmentLink(id);
  const src = link.entry ? link.url : localSrc;
  const box = attachmentImageBox(link.entry, MESSAGE_IMAGE_BLOCK);
  const alt = name || t('chat.messageAttachments.image');

  return (
    <AttachmentPrimitive.Root className="flex-shrink-0">
      <div
        className="overflow-hidden"
        style={{
          maxWidth: MESSAGE_IMAGE_BLOCK.width,
          borderRadius: 20,
          background: 'rgba(0,0,0,0.04)',
          ...(box ? { width: box.width, height: box.height } : {}),
        }}
      >
        {src ? (
          <img
            src={src}
            alt={alt}
            className="w-full h-auto object-cover"
            referrerPolicy="no-referrer"
            onError={link.reportError}
            style={
              box
                ? { width: '100%', height: '100%', display: 'block' }
                : { maxHeight: MESSAGE_IMAGE_BLOCK.height, minHeight: 80 }
            }
          />
        ) : (
          <div
            className={box ? 'w-full h-full flex items-center justify-center' : 'w-[200px] h-[120px] flex items-center justify-center'}
            style={{ backgroundColor: 'rgba(0,0,0,0.04)' }}
            role="img"
            aria-label={alt}
          >
            <FileImage size={32} style={{ color: 'var(--text-tertiary)' }} />
          </div>
        )}
      </div>
    </AttachmentPrimitive.Root>
  );
};

/**
 * A sent file: its name and, from the history, its size; a PDF with a
 * signed link downloads when clicked.
 */
const MessageDocAttachment: FC = () => {
  const { t, i18n } = useTranslation('common');
  const name = useAuiState((s) => s.attachment.name);
  const id = useAuiState((s) => s.attachment.id);
  const contentType = useAuiState((s) => s.attachment.contentType);
  const link = useAgentAttachmentLink(id);
  const label = name || t('chat.messageAttachments.pdf');
  const ext = getFileExtension(name) || (contentType === 'application/pdf' ? 'PDF' : '');
  const size = link.entry ? formatAttachmentBytes(link.entry.bytes, i18n.language || 'en') : '';
  const meta = [ext, size].filter(Boolean).join(' · ');
  const { icon: Icon, color, bg } = getFileTypeInfo(name || 'file.pdf', contentType);

  const chip = (
    <div
      className="flex items-center gap-3 px-3.5 py-3 transition-colors"
      style={{
        backgroundColor: 'rgba(0,0,0,0.04)',
        borderRadius: 20,
        minWidth: 200,
        maxWidth: 280,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.06)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.04)'; }}
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: bg }}
      >
        <Icon size={20} style={{ color }} />
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <span
          className="text-[13px] font-medium truncate leading-tight"
          style={{ color: 'var(--text-primary)' }}
          dir="auto"
        >
          {label}
        </span>
        {meta ? (
          <span className="text-[11px] leading-tight mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
            {meta}
          </span>
        ) : null}
      </div>
    </div>
  );

  return (
    <AttachmentPrimitive.Root>
      {link.url ? (
        <a
          href={link.url}
          download={name || undefined}
          target="_blank"
          rel="noreferrer"
          aria-label={t('chat.messageAttachments.download', { name: label })}
          style={{ textDecoration: 'none' }}
        >
          {chip}
        </a>
      ) : (
        chip
      )}
    </AttachmentPrimitive.Root>
  );
};

const messageAttachmentComponents = {
  Image: MessageImageAttachment,
  Document: MessageDocAttachment,
  File: MessageDocAttachment,
};

const UserMessageAttachments: FC = () => {
  return (
    <div className="flex flex-row flex-wrap justify-end gap-2 mb-2 empty:hidden">
      <MessagePrimitive.Attachments components={messageAttachmentComponents} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Composer (shared between empty state and viewport footer)
// ---------------------------------------------------------------------------
/**
 * Opens live mode (talk to Nova) from the web composers, the twin of the
 * sound-wave button in the native composer. Carries the open chat thread and
 * the agent mode so the conversation continues there.
 */
const LiveModeButton: FC = () => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const pathname = usePathname();
  const { snapshot } = useSubscriptionState();
  const threadMatch = /^\/chat\/([^/]+)/.exec(pathname ?? '');
  const threadId = threadMatch ? decodeURIComponent(threadMatch[1]) : undefined;
  return (
    <button
      type="button"
      className="tabbio-send-btn"
      onClick={() =>
        router.push({
          pathname: '/voice-handsfree',
          params: {
            role: snapshot?.planSet === 'employer' ? 'employer' : 'seeker',
            ...(threadId ? { threadId } : {}),
          },
        } as never)
      }
      aria-label={t('voice.handsfree.title')}
      title={t('voice.handsfree.title')}
    >
      <MaterialSymbol name="graphic_eq" />
    </button>
  );
};

const WebComposer: FC = () => {
  const isMobile = useWebDrawer()?.isMobile ?? false;
  const { t } = useTranslation('common');
  const composerText = useAuiState((s) => s.composer.text);
  const isRunning = useAuiState((s) => s.thread.isRunning);
  const { snapshot } = useSubscriptionState();
  const upgradeModal = useUpgradeModal();
  const pathname = usePathname();
  const chatActionKey: AIActionKey =
    snapshot?.planSet === 'employer' ? 'employer.workspace_basic_ai' : 'individual.career_agent_chat';
  const chatGuard = useAiActionGuard(chatActionKey);
  const aiLimitBanner = selectAiCreditLimitBanner(
    chatGuard.decision,
    chatGuard.canProceed,
  );
  const isAiLimitReached = aiLimitBanner.visible;
  const isSkillCommand =
    snapshot?.planSet !== 'employer' &&
    /^\/[a-z0-9]+(?:-[a-z0-9]+)*(?:\s|$)/i.test(composerText.trim());
  const openChatUpgrade = useCallback(
    () =>
      upgradeModal.open(
        createChatUpgradeContext({
          decision: chatGuard.decision,
          entryPoint: 'chat.web_composer',
          planSet: snapshot?.planSet,
          returnTo: pathname,
        }),
      ),
    [chatGuard.decision, pathname, snapshot?.planSet, upgradeModal],
  );

  // Track the stream lifecycle so the limit banner stays accurate without waiting
  // for the 15s React Query staleTime: optimistically count on stream start,
  // reconcile against the server when the stream finishes.
  const previousIsRunningRef = useRef(isRunning);
  const chatGuardRef = useRef(chatGuard);
  chatGuardRef.current = chatGuard;
  useEffect(() => {
    const wasRunning = previousIsRunningRef.current;
    previousIsRunningRef.current = isRunning;
    const guard = chatGuardRef.current;
    if (!wasRunning && isRunning) {
      guard.beginOptimisticAction(guard.estimatedCredits);
    } else if (wasRunning && !isRunning) {
      void guard.reconcile().finally(() => guard.clearOptimisticActions());
    }
  }, [isRunning]);

  return (
    <div
      // Width comes from `--tabbio-chat-measure` (global.css), shared with the
      // messages so the composer and the answers line up.
      className="tabbio-web-composer w-full mx-auto px-4"
    >
      {isAiLimitReached && !isSkillCommand && (
        <button
          type="button"
          onClick={openChatUpgrade}
          aria-label={t(aiLimitBanner.titleKey, aiLimitBanner.values)}
          className="w-full mb-2 text-center transition-opacity hover:opacity-90"
          style={{
            backgroundColor: 'var(--menu-bg)',
            boxShadow: 'var(--gem-input-shadow)',
            borderRadius: 9999,
            padding: '12px 16px',
            border: 0,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--text-primary)',
            }}
          >
            {t(aiLimitBanner.titleKey, aiLimitBanner.values)}
          </div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--text-tertiary)',
              marginTop: 2,
            }}
          >
            {t(aiLimitBanner.hintKey, aiLimitBanner.values)}
          </div>
        </button>
      )}
      <ComposerPrimitive.Root className="relative flex w-full flex-col">
        <ComposerPrimitive.AttachmentDropzone asChild>
          <div>
            <ComposerBar
              variant="pill"
              placeholder={t('chat.askAnything')}
              autoFocus={!isMobile}
              showModelPicker={!isMobile}
              phoneControls={isMobile}
              focusInputOnMenu={!isMobile}
              employer={snapshot?.planSet === 'employer'}
              isSkillCommand={isSkillCommand}
              limitReached={isAiLimitReached}
              limitLabel={t(aiLimitBanner.titleKey, aiLimitBanner.values)}
              onLimitReached={openChatUpgrade}
              liveModeButton={<LiveModeButton />}
            />
          </div>
        </ComposerPrimitive.AttachmentDropzone>
      </ComposerPrimitive.Root>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Empty State: Perplexity-style centered home (wordmark + composer + Try Tabbio)
// ---------------------------------------------------------------------------

// Seeker (personal) categories: backed by cv.*, application.*, catalog.*, profile.* tools
type SeekerActionKey =
  | 'webcv'
  | 'tailor'
  | 'jobs'
  | 'apply'
  | 'autoapply'
  | 'interview'
  | 'linkedin';

// Company categories: backed by job.*, candidate.*, message.*, company.* tools
type CompanyActionKey =
  | 'postjob'
  | 'shortlist'
  | 'talent'
  | 'outreach'
  | 'schedule'
  | 'careerpage'
  | 'analytics';

type QuickActionKey = SeekerActionKey | CompanyActionKey;


const HomeComposer: FC = () => {
  const { t } = useTranslation(['common', 'seeker']);
  const isMobile = useWebDrawer()?.isMobile ?? false;
  const composerText = useAuiState((s) => s.composer.text);
  const isRunning = useAuiState((s) => s.thread.isRunning);
  const { snapshot } = useSubscriptionState();
  const upgradeModal = useUpgradeModal();
  const pathname = usePathname();
  const chatActionKey: AIActionKey =
    snapshot?.planSet === 'employer' ? 'employer.workspace_basic_ai' : 'individual.career_agent_chat';
  const chatGuard = useAiActionGuard(chatActionKey);
  const aiLimitBanner = selectAiCreditLimitBanner(
    chatGuard.decision,
    chatGuard.canProceed,
  );
  const isAiLimitReached = aiLimitBanner.visible;
  const isSkillCommand =
    snapshot?.planSet !== 'employer' &&
    /^\/[a-z0-9]+(?:-[a-z0-9]+)*(?:\s|$)/i.test(composerText.trim());
  // Tag naming the page the agent will act on when this message is sent. The
  // visible surface of the page-context envelope. Employer mode has no catalog.
  // Hidden on every screen for now (owner, 2026-10-04); the envelope itself
  // still rides with each message. Flip SHOW_COMPOSER_SCREEN_TAG to restore it.
  const screenTag =
    snapshot?.planSet === 'employer'
      ? undefined
      : getScreenTag(derivePageContextScreen(pathname), pathname);
  const openChatUpgrade = useCallback(
    () =>
      upgradeModal.open(
        createChatUpgradeContext({
          decision: chatGuard.decision,
          entryPoint: 'chat.web_home_composer',
          planSet: snapshot?.planSet,
          returnTo: pathname,
        }),
      ),
    [chatGuard.decision, pathname, snapshot?.planSet, upgradeModal],
  );

  const previousIsRunningRef = useRef(isRunning);
  const chatGuardRef = useRef(chatGuard);
  chatGuardRef.current = chatGuard;
  useEffect(() => {
    const wasRunning = previousIsRunningRef.current;
    previousIsRunningRef.current = isRunning;
    const guard = chatGuardRef.current;
    if (!wasRunning && isRunning) {
      guard.beginOptimisticAction(guard.estimatedCredits);
    } else if (wasRunning && !isRunning) {
      void guard.reconcile().finally(() => guard.clearOptimisticActions());
    }
  }, [isRunning]);

  return (
    <ComposerPrimitive.Root className="block">
      <ComposerPrimitive.AttachmentDropzone asChild>
        <div>
          {/* The hero: the input on its own rows, the controls beneath. */}
          <ComposerBar
            variant="tall"
            placeholder={t('chat.home.placeholder')}
            autoFocus
            focusInputOnMenu={!isMobile}
            employer={snapshot?.planSet === 'employer'}
            isSkillCommand={isSkillCommand}
            limitReached={isAiLimitReached}
            limitLabel={t(aiLimitBanner.titleKey, aiLimitBanner.values)}
            onLimitReached={openChatUpgrade}
            liveModeButton={<LiveModeButton />}
            header={
              // Which page the agent will act on when you send: the visible
              // surface of the page-context envelope that rides with the
              // message.
              SHOW_COMPOSER_SCREEN_TAG && screenTag ? (
                <div className="tabbio-composer-tags">
                  <span className="tabbio-composer-tag">
                    <MaterialSymbol name={screenTag.icon} size={14} />
                    {t(`seeker:${screenTag.labelKey}`)}
                  </span>
                </div>
              ) : null
            }
          />

          {isAiLimitReached && !isSkillCommand && (
            <button
              type="button"
              onClick={openChatUpgrade}
              aria-label={t(aiLimitBanner.titleKey, aiLimitBanner.values)}
              className="w-full mt-5 rounded-full px-4 py-2.5 text-center transition-opacity hover:opacity-90"
              style={{
                backgroundColor: 'var(--menu-bg)',
                boxShadow: 'var(--gem-input-shadow)',
                border: 0,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                }}
              >
                {t(aiLimitBanner.titleKey, aiLimitBanner.values)}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--text-tertiary)',
                  marginTop: 2,
                }}
              >
                {t(aiLimitBanner.hintKey, aiLimitBanner.values)}
              </div>
            </button>
          )}
        </div>
      </ComposerPrimitive.AttachmentDropzone>
    </ComposerPrimitive.Root>
  );
};

// Material Symbol names for quick-action chips on the home suggest card.
const QUICK_ACTION_GEM_ICONS: Record<QuickActionKey, string> = {
  webcv: 'description',
  tailor: 'edit_document',
  jobs: 'work',
  apply: 'task_alt',
  autoapply: 'bolt',
  interview: 'lightbulb',
  linkedin: 'person',
  postjob: 'work',
  shortlist: 'task_alt',
  talent: 'group',
  outreach: 'send',
  schedule: 'event',
  careerpage: 'description',
  analytics: 'analytics',
};

type HeroActionIcon = FC<{ size?: number; color?: string }>;

type HeroAction = {
  titleKey: string;
  prompt: string;
  Icon: HeroActionIcon;
  tint: string;
};

// The four tints are shared by both workspace modes so the row reads as one
// family (matches the native chip palette).
const HERO_ACTION_TINTS = ['#A379FE', '#7986FE', '#79D2FE', '#799FFE'] as const;

// Seeker starters: four web entry points. The prompt each pill sends is the
// shared `chat.home.starterPrompts` copy (the same keys native sends), so an
// Arabic session asks in Arabic instead of a hard-coded English sentence.
type SeekerHeroAction = Omit<HeroAction, 'prompt'> & { promptKey: string };

const SEEKER_HERO_ACTIONS: SeekerHeroAction[] = [
  {
    titleKey: 'chat.suggestions.buildCV',
    promptKey: 'matchJobs',
    Icon: CVIcon,
    tint: HERO_ACTION_TINTS[0],
  },
  {
    titleKey: 'chat.suggestions.findJobs',
    promptKey: 'matchSkills',
    Icon: JobIcon,
    tint: HERO_ACTION_TINTS[1],
  },
  {
    titleKey: 'chat.suggestions.tailorCV',
    promptKey: 'tailorCv',
    Icon: TailorCVIcon,
    tint: HERO_ACTION_TINTS[2],
  },
  {
    titleKey: 'chat.suggestions.prepInterview',
    promptKey: 'interview',
    Icon: InterviewIcon,
    tint: HERO_ACTION_TINTS[3],
  },
];

// Employer starters share the same company action keys and copy as native and
// render with Material Symbols in the same pill shape and tint ramp.
function symbolHeroIcon(name: string): HeroActionIcon {
  return function SymbolHeroIcon({ size = 18, color }) {
    return <MaterialSymbol name={name} size={size} style={{ color }} />;
  };
}

const COMPANY_HERO_ACTION_KEYS: CompanyActionKey[] = [
  'postjob',
  'talent',
  'shortlist',
  'outreach',
];

const HomeHero: FC = () => {
  const isMobile = useWebDrawer()?.isMobile ?? false;
  const { t } = useTranslation('common');
  const { t: tSeeker } = useTranslation('seeker');
  const router = useRouter();
  const aui = useAui();
  const isAiRunning = useAuiState((s) => s.thread.isRunning);
  const { user } = useAuth();
  const { activeWorkspace } = useWorkspace();

  const isCompany = activeWorkspace.type === 'company';

  // Which screen the content panel is showing. Employer mode resolves against
  // its own catalog (route-context-intros.employer.ts); 'chat'/'other' fall
  // through to the generic pills in both modes.
  const pathname = usePathname();
  const routeIntroScreen = useMemo(() => {
    if (isCompany) return getEmployerRouteIntroKey(pathname);
    const screen = derivePageContextScreen(pathname);
    return getRouteIntro(screen, pathname) ? screen : null;
  }, [isCompany, pathname]);

  // The hero only centres in the chat-only workspace. Beside a page or a file
  // it docks to the bottom edge (global.css) and the pills sit above the
  // composer, so they render first there: tab and reading order follow what is
  // on screen instead of a CSS `order` swap.
  const artifactPanel = useShownArtifactPanel(useArtifactPanel());
  const heroDocked = !getWebWorkspaceClassName(pathname, artifactPanel).includes('is-chat-only');

  const sendQuickPrompt = useCallback(
    (prompt: string) => {
      if (isAiRunning) return;
      const send = () => {
        const composer = aui.composer();
        composer.setText(prompt);
        composer.send();
      };

      if (!runIfComposerAvailable(send) && typeof window !== 'undefined') {
        window.setTimeout(() => {
          runIfComposerAvailable(send);
        }, 0);
      }
    },
    [aui, isAiRunning],
  );

  // Company pills are assembled from the shared action copy; seeker pills are
  // the fixed web set with localized prompts. `titleKey` is resolved against
  // the matching namespace at render time (seeker keys for one, common for the
  // other).
  const heroActions: HeroAction[] = useMemo(() => {
    if (!isCompany) {
      return SEEKER_HERO_ACTIONS.map(({ promptKey, ...action }) => ({
        ...action,
        prompt: t(`chat.home.starterPrompts.${promptKey}`),
      }));
    }
    return COMPANY_HERO_ACTION_KEYS.map((key, index) => {
      const promptsRaw = t(`chat.home.actions.${key}.prompts`, {
        returnObjects: true,
      }) as unknown;
      const first = Array.isArray(promptsRaw)
        ? (promptsRaw as unknown[]).find((p) => typeof p === 'string')
        : undefined;
      return {
        titleKey: `chat.home.actions.${key}.label`,
        prompt: (first as string) ?? t(`chat.home.actions.${key}.label`),
        Icon: symbolHeroIcon(QUICK_ACTION_GEM_ICONS[key]),
        tint: HERO_ACTION_TINTS[index % HERO_ACTION_TINTS.length],
      };
    });
  }, [isCompany, t]);

  const resolveHeroLabel = isCompany ? t : tSeeker;

  const greetingKey = (() => {
    const h = new Date().getHours();
    if (h < 5) return 'night';
    if (h < 12) return 'morning';
    if (h < 17) return 'afternoon';
    if (h < 22) return 'evening';
    return 'night';
  })();
  const greetingText = t(`chat.home.greeting.${greetingKey}`, {
    defaultValue: t('chat.home.greeting.fallback'),
  });
  const firstName = (() => {
    const raw = (user?.name || '').trim();
    if (!raw) return '';
    return raw.split(/\s+/)[0];
  })();

  const handleTemporaryChat = () => {
    runIfComposerAvailable(() => {
      const composer = aui.composer();
      composer.setText('');
    });
  };

  if (isMobile) {
    return <MobileWebHome name={firstName} onSend={sendQuickPrompt} disabled={isAiRunning}
      companyActions={isCompany ? heroActions.map(action => ({ ...action, label: t(action.titleKey) })) : undefined}>
      <WebComposer />
    </MobileWebHome>;
  }

  // On a screen the agent can act on, the contextual intro above the composer
  // replaces the generic pills. Desktop quick actions stay on one line.
  const heroPills = routeIntroScreen ? null : (
    <div className="tabbio-hero-actions">
      {heroActions.map((action) => (
        <button
          key={action.titleKey}
          type="button"
          className="tabbio-hero-action"
          onClick={() => sendQuickPrompt(action.prompt)}
          disabled={isAiRunning}
        >
          <action.Icon size={18} color={action.tint} />
          {resolveHeroLabel(action.titleKey)}
        </button>
      ))}
    </div>
  );

  return (
    <main className="tabbio-main">
      <div className="tabbio-hero">
        {/* Tabbio's new-chat cloud, built like Gemini's glow: its two layers
            are this box's ::before and ::after. Decorative only; global.css
            shows it on the centred chat-only hero. */}
        <div className="tabbio-hero-aura" aria-hidden="true" />
        {/* Centred (chat-only): greeting, composer, then the quick actions
            under the composer, as on Gemini's new-chat screen. Docked beside a
            page or a file: the pills come before the composer instead. */}
        <div className="tabbio-hero-center">
          <h1 className="tabbio-greeting">
            <span className="tabbio-greeting-lead">
              {greetingText}
              {firstName ? ',' : ''}
            </span>
            {/* Only the name ellipsises. The greeting itself always reads in
                full however long the name is. */}
            {firstName ? (
              <span className="tabbio-greeting-name" dir="auto">
                {firstName}
              </span>
            ) : null}
          </h1>
        </div>

        {heroDocked ? heroPills : null}

        <div className="tabbio-input-wrap">
          {routeIntroScreen ? (
            <div className="tabbio-intro-dock">
              <RouteContextIntro
                // Remount on screen change so the typewriter replays for the
                // new copy instead of resuming mid-string.
                key={`${routeIntroScreen}:${pathname}`}
                screen={routeIntroScreen}
                pathname={pathname}
                mode={isCompany ? 'employer' : 'seeker'}
              />
            </div>
          ) : null}
          <HomeComposer />
        </div>

        {heroDocked ? null : heroPills}
      </div>
    </main>
  );
};

const EmptyState: FC = () => <HomeHero />;

// ---------------------------------------------------------------------------
// Scroll to Bottom
// ---------------------------------------------------------------------------
const ScrollToBottom: FC = () => {
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <button
        className="absolute -top-10 left-1/2 z-10 -translate-x-1/2 transition-colors disabled:invisible"
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          backgroundColor: 'var(--menu-bg)',
          boxShadow: 'var(--gem-input-shadow)',
          color: 'var(--text-secondary)',
          border: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <MaterialSymbol name="arrow_downward" size={18} />
      </button>
    </ThreadPrimitive.ScrollToBottom>
  );
};

// ---------------------------------------------------------------------------
// Branch Picker
// ---------------------------------------------------------------------------
const WebBranchPicker: FC = () => {
  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className="inline-flex items-center text-xs me-2"
      style={{ color: 'var(--text-tertiary)', gap: 2 }}
    >
      <BranchPickerPrimitive.Previous asChild>
        <button
          className="rounded-full transition-colors disabled:opacity-30"
          style={{
            width: 28,
            height: 28,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 0,
            color: 'var(--text-tertiary)',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.06)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <MaterialSymbol name="chevron_left" size={16} />
        </button>
      </BranchPickerPrimitive.Previous>
      <span style={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <button
          className="rounded-full transition-colors disabled:opacity-30"
          style={{
            width: 28,
            height: 28,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 0,
            color: 'var(--text-tertiary)',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.06)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <MaterialSymbol name="chevron_right" size={16} />
        </button>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
};

// ---------------------------------------------------------------------------
// Action Bar
// ---------------------------------------------------------------------------
const WebActionBar: FC = () => {
  const toast = useToast();
  const { t } = useTranslation('seeker');
  const { mode, companyId } = useWorkspace();
  const queryClient = useQueryClient();
  const handleClickCopy = useCallback(() => {
    toast.success(t('cv.previewScreen.copied'));
  }, [toast, t]);

  const custom = useAuiState((s) => s.message.metadata.custom);
  const fallbackId = useAuiState((s) => s.message.id);
  const threadId = custom.threadId as string | undefined;
  const messageId = (custom.messageId as string | undefined) ?? fallbackId;
  const initialFeedback = (custom.feedback as MessageFeedbackValue | null | undefined) ?? null;

  const [feedback, setFeedback] = useState<MessageFeedbackValue | null>(initialFeedback);
  const [pending, setPending] = useState(false);

  // Web's message list keys by index, not id, so a regenerated/branch-switched
  // response can reuse this same component instance - re-seed local state
  // whenever the resolved message id changes (native remounts anyway, so this
  // is a harmless no-op there).
  useEffect(() => {
    setFeedback(initialFeedback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId]);

  const handleFeedback = async (value: MessageFeedbackValue) => {
    if (!threadId || !messageId || pending) return;
    const previous = feedback;
    const next = previous === value ? null : value;
    setFeedback(next);
    setPending(true);
    try {
      if (next === null) {
        await clearMessageFeedback(threadId, messageId, mode, companyId);
      } else {
        await setMessageFeedback(threadId, messageId, next, mode, companyId);
      }
      queryClient.setQueryData(
        QUERY_KEYS.THREAD_MESSAGES(threadId, mode, companyId),
        (old: any) =>
          old
            ? {
                ...old,
                messages: old.messages.map((m: any) =>
                  m.id === messageId ? { ...m, feedback: next } : m,
                ),
              }
            : old,
      );
    } catch {
      setFeedback(previous);
      toast.error(t('chat.feedback.updateFailed'));
    } finally {
      setPending(false);
    }
  };


  // A turn rebuilt to hold a pending approval: nothing to copy, rate or regenerate yet.
  if (custom.approvalTurn) return null;
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      className="flex"
      style={{ color: 'var(--text-tertiary)', gap: 2 }}
    >
      <ActionBarPrimitive.Copy asChild>
        <button
          type="button"
          aria-label={t('common:skills.runDialog.copy')}
          className="tabbio-msg-action"
          onClick={handleClickCopy}
        >
          <AuiIf condition={(s) => s.message.isCopied}>
            <MaterialSymbol name="check" size={15} />
          </AuiIf>
          <AuiIf condition={(s) => !s.message.isCopied}>
            <MaterialSymbol name="content_copy" size={15} />
          </AuiIf>
        </button>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.Reload asChild>
        <button
          type="button"
          aria-label={t('common:skills.runDialog.regenerate')}
          className="tabbio-msg-action"
        >
          <MaterialSymbol name="refresh" size={15} />
        </button>
      </ActionBarPrimitive.Reload>
      {threadId && messageId ? (
        <>
          <button
            type="button"
            className="tabbio-msg-action"
            style={{
              // Selected reads as filled + primary, not green: this control
              // reports a preference, not an outcome.
              fontVariationSettings: feedback === 'up' ? "'FILL' 1" : undefined,
            }}
            onClick={() => handleFeedback('up')}
            disabled={pending}
            aria-pressed={feedback === 'up'}
            aria-label={t('chat.feedback.goodResponse')}
          >
            <MaterialSymbol name="thumb_up" size={15} />
          </button>
          <button
            type="button"
            className="tabbio-msg-action"
            style={{
              fontVariationSettings: feedback === 'down' ? "'FILL' 1" : undefined,
            }}
            onClick={() => handleFeedback('down')}
            disabled={pending}
            aria-pressed={feedback === 'down'}
            aria-label={t('chat.feedback.badResponse')}
          >
            <MaterialSymbol name="thumb_down" size={15} />
          </button>
        </>
      ) : null}
    </ActionBarPrimitive.Root>
  );
};

// ---------------------------------------------------------------------------
// User Message
// ---------------------------------------------------------------------------
const UserActionBar: FC = () => {
  const { t } = useTranslation('seeker');
  const aui = useAui();
  const isEditing = useAuiState((s) => s.composer.isEditing);
  const iconButtonStyle = {
    width: 28,
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    background: 'transparent',
    border: 0,
    color: 'var(--text-tertiary)',
    cursor: 'pointer',
    transition: 'background-color 120ms, opacity 120ms',
  } as const;
  const handleEdit = useCallback(() => {
    if (isEditing) return;

    try {
      runIfMessageEditAvailable(() => {
        aui.composer().beginEdit();
      });
    } catch (error) {
      captureSentryException(error, { scope: 'chat_edit_begin' });
    }
  }, [aui, isEditing]);

  return (
    <ActionBarPrimitive.Root className="flex" style={{ gap: 2 }}>
      <button
        type="button"
        style={{
          ...iconButtonStyle,
          cursor: isEditing ? 'default' : 'pointer',
          opacity: isEditing ? 0.45 : 1,
        }}
        aria-label={t('chat.editPrompt')}
        disabled={isEditing}
        onClick={handleEdit}
        onMouseEnter={(e) => {
          if (!isEditing) e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.06)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <Pencil size={14} />
      </button>
    </ActionBarPrimitive.Root>
  );
};

// The user's own words as sent: mentions the runtime removed before sending
// are removed here too, so the bubble matches the stored message on reload.
const UserMessageText: TextMessagePartComponent = ({ text }) => {
  const displayText = useAgentMessageDisplayText(text);
  return <p style={{ whiteSpace: 'pre-line' }}>{displayText}</p>;
};

const userMessagePartComponents = { Text: UserMessageText };

const UserMessage: FC = () => {
  return (
    <MessagePrimitive.Root className="mx-auto w-full max-w-3xl py-3 px-4 group">
      <div className="flex flex-col items-end w-full">
        <UserMessageAttachments />
        <div
          dir="auto"
          className="px-5 py-2.5 rounded-3xl max-w-[70%] text-[15px] leading-relaxed"
          style={{
            backgroundColor: 'var(--user-message-bg)',
            color: 'var(--text-primary)',
          }}
        >
          <MessagePrimitive.Parts components={userMessagePartComponents} />
        </div>
        <div className="mt-1 me-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <WebBranchPicker />
          <UserActionBar />
        </div>
      </div>
    </MessagePrimitive.Root>
  );
};

const UserEditComposerWeb: FC = () => {
  const { t } = useTranslation('seeker');
  const toast = useToast();
  const aui = useAui();
  const composerText = useAuiState((s) => s.composer.text);
  const originalText = useAuiState((s) => {
    const content = (s.message.content ?? []) as { type: string; text?: string }[];
    return content
      .filter((p) => p.type === 'text' && typeof p.text === 'string')
      .map((p) => p.text ?? '')
      .join('');
  });
  const isRunning = useAuiState((s) => s.thread.isRunning);
  const canSend =
    composerText.trim().length > 0 && !isRunning && composerText !== originalText;

  const handleSend = useCallback(async () => {
    try {
      await aui.composer().send();
    } catch (error) {
      captureSentryException(error, { scope: 'chat_edit_composer_send' });
      toast.error(t('chat.editSendFailedTitle'), t('chat.editSendFailedMessage'));
    }
  }, [aui, t, toast]);

  return (
    <MessagePrimitive.Root className="mx-auto w-full max-w-3xl py-3 px-4">
      <div className="flex flex-col items-end w-full">
        <ComposerPrimitive.Root
          className="w-full max-w-[70%] rounded-3xl px-5 py-3"
          style={{
            backgroundColor: 'var(--user-message-bg)',
            color: 'var(--text-primary)',
          }}
        >
          <ComposerPrimitive.Input
            dir="auto"
            submitMode="enter"
            autoFocus
            className="w-full bg-transparent outline-none resize-none text-[15px] leading-relaxed min-h-[24px] max-h-[200px]"
            style={{ color: 'var(--text-primary)', fontFamily: 'inherit' }}
          />
          <div className="mt-2 flex flex-row justify-end gap-2">
            <ComposerPrimitive.Cancel asChild>
              <button
                type="button"
                className="px-4 py-1.5 rounded-full text-[13px] font-medium transition-opacity hover:opacity-80"
                style={{
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  border: 0,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {t('chat.cancelEdit')}
              </button>
            </ComposerPrimitive.Cancel>
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              className="px-4 py-1.5 rounded-full text-[13px] font-semibold transition-opacity"
              style={{
                background: 'var(--text-primary)',
                color: 'var(--bg)',
                border: 0,
                cursor: canSend ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                opacity: canSend ? 1 : 0.4,
              }}
            >
              {t('chat.saveEdit')}
            </button>
          </div>
        </ComposerPrimitive.Root>
      </div>
    </MessagePrimitive.Root>
  );
};

// ---------------------------------------------------------------------------
// Assistant identity
//
// Replaces two things that used to sit on every assistant turn: the 24px
// NovaAvatar + "Career agent" in 17px semibold at the top, and the
// `[logo] Tabbio · <tool>` row above each card.
//
// The tool row is gone entirely. Its label was the API's `display.title`
// ("Skills List"), which the activity trace now says properly, and its logo
// never rendered at all: `require()` of a PNG returns a Metro module object,
// not a URL, so the `src` was always garbage. It was also a *second* brand mark
// twelve pixels below the first, at a different size.
//
// What's left is one quiet attribution beside the message actions.
// ---------------------------------------------------------------------------
const AssistantIdentity: FC = () => {
  const { t } = useTranslation('common');

  return (
    <span
      className="flex items-center gap-1.5 me-1 shrink-0"
      style={{ color: 'var(--text-tertiary)', fontSize: 12, lineHeight: '16px' }}
    >
      <NovaAvatar size={18} />
      {t('assistantName', { defaultValue: 'Career agent' })}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Markdown config
// ---------------------------------------------------------------------------
const remarkPlugins = [remarkGfm];

// Prefix assistant-message section headings with the Nova sparkle glyph (Figma).
function SparkleHeading({
  level,
  children,
}: {
  level: 1 | 2 | 3;
  children?: React.ReactNode;
}) {
  const Tag = `h${level}` as 'h1' | 'h2' | 'h3';
  return (
    <Tag style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
      <span aria-hidden style={{ flexShrink: 0, marginTop: 3, lineHeight: 0 }}>
        <NovaSparkle size={18} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
    </Tag>
  );
}

const markdownComponents = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <SparkleHeading level={1}>{children}</SparkleHeading>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <SparkleHeading level={2}>{children}</SparkleHeading>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <SparkleHeading level={3}>{children}</SparkleHeading>
  ),
  a: MarkdownLinkChip,
};

// ---------------------------------------------------------------------------
// Assistant Message (ChatGPT Apps-style: label + card hero + brief follow-up)
// ---------------------------------------------------------------------------
const AssistantMessage: FC = () => {
  const parts = useAuiState((s) => s.message.content);
  const isRunning = useAuiState((s) => s.message.status?.type === 'running');
  const messageIsLast = useAuiState((s) => s.message.isLast);
  // A second branch of the same message is a regeneration: the loader wears
  // its "rewriting" face while it runs.
  const branchNumber = useAuiState((s) => s.message.branchNumber);
  const regenerating = isRunning && branchNumber > 1;
  const hasToolCalls = parts?.some((p: any) => p.type === 'tool-call') ?? false;
  const toolCallSeenRef = useRef(false);
  const partIndexRef = useRef(0);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  // Which tool "drives" the follow-up suggestions when a turn has multiple
  // tool calls: the last non-hidden one, matching the backend's own "take the
  // last new assistant message" convention for the same turn.
  const drivingToolName = ((parts ?? []) as any[])
    .filter((p) => p.type === 'tool-call' && p.toolName && !HIDDEN_TOOLS.has(p.toolName))
    .at(-1)?.toolName as string | undefined;

  // The clock starts the first time this message renders as running and stops
  // when it settles. A rehydrated message never ran here, so it has no timing
  // and its trace header omits the duration rather than inventing one.
  const timing = useRunTiming(isRunning);

  // Derived by the shared model so web and native cannot disagree about what
  // the steps and sources are (see activity-trace-model.ts).
  const traceParts = useMemo(() => (parts ?? []) as TracePart[], [parts]);
  const sources = useMemo(
    () => (isRunning ? [] : deriveSources(traceParts, HIDDEN_TOOLS)),
    [traceParts, isRunning],
  );

  // Reset tracking refs each render
  toolCallSeenRef.current = false;
  partIndexRef.current = 0;

  return (
    <MessagePrimitive.Root className="mx-auto w-full max-w-3xl py-3 px-4">
      <div className="w-full min-w-0">
        <div
          dir="auto"
          style={{
            color: 'var(--text-primary)',
            // Cairo sits after the Latin faces so Arabic answers (which the
            // Latin faces have no glyphs for) resolve to Cairo instead of an
            // arbitrary system fallback (issue #163).
            fontFamily:
              "'GoogleSans_400Regular', 'Google Sans', 'Cairo', -apple-system, BlinkMacSystemFont, ui-sans-serif, sans-serif",
          }}
        >
          {/* The activity trace: the pixel loader while nothing has arrived,
              then one expandable block with a live header and one row per
              tool call. Cards render below, in the parts loop, because a card
              is content while a step is process: folding the trace should
              hide the narration, never the result. */}
          <ActivityTrace parts={traceParts} isRunning={isRunning} timing={timing} regenerating={regenerating} />

          <MessagePrimitive.Parts>
            {({ part }) => {
              const idx = partIndexRef.current++;

              if (part.type === 'tool-call') {
                toolCallSeenRef.current = true;
                const p = part as any;

                // Skip hidden/internal tools entirely (no step, no card)
                if (HIDDEN_TOOLS.has(p.toolName)) return null;

                return (
                  <div className="tool-card-group">
                    <ToolCardRenderer
                      toolCallId={p.toolCallId}
                      toolName={p.toolName}
                      cardType={p.cardType}
                      args={p.args ?? {}}
                      result={p.result}
                      isError={p.isError}
                      // A call still "running" after the turn ended never got
                      // a result; it must not sit as a skeleton forever.
                      stage={(settleStage(p.stage, isRunning) ?? 'running') as any}
                      display={p.display}
                      category={p.category}
                      entityType={p.entityType}
                      entityId={p.entityId}
                      approvalId={p.approvalId}
                    />
                  </div>
                );
              }

              if (part.type === 'text') {
                const isFollowUp = hasToolCalls && toolCallSeenRef.current;
                const isLast = idx === (parts?.length ?? 0) - 1;
                const streaming = isRunning && isLast;
                const baseClass = isFollowUp ? 'aui-md-followup' : 'aui-md-root';
                const streamClass = streaming ? ' aui-text-streaming' : '';

                return (
                  <div className={`${baseClass}${streamClass} text-[15px] leading-[1.7]`}>
                    <MarkdownTextPrimitive
                      remarkPlugins={remarkPlugins}
                      // Words resolve out of blur while this block is the one
                      // still arriving; the plugin is dropped once it settles.
                      rehypePlugins={streaming ? STREAMING_REHYPE_PLUGINS : undefined}
                      components={markdownComponents}
                      smooth
                    />
                  </div>
                );
              }

              return null;
            }}
          </MessagePrimitive.Parts>
        </div>

        {/* Actions, sources and follow-ups become usable together once the
            answer is complete; they fade in rather than popping. */}
        {!isRunning ? (
          <div className="tabbio-msg-actions">
            <AssistantIdentity />
            <WebBranchPicker />
            <WebActionBar />
            <SourcesToggle
              sources={sources}
              open={sourcesOpen}
              onToggle={() => setSourcesOpen((value) => !value)}
            />
          </div>
        ) : null}
        {!isRunning ? <SourcesList sources={sources} open={sourcesOpen} /> : null}
        {!isRunning && messageIsLast ? <FollowUpList toolName={drivingToolName} /> : null}
      </div>
    </MessagePrimitive.Root>
  );
};

// ---------------------------------------------------------------------------
// Message Router
// ---------------------------------------------------------------------------
const WebMessage: FC = () => {
  const role = useAuiState((s) => s.message.role);
  const isEditing = useAuiState((s) => s.message.composer.isEditing);
  if (role === 'user' && isEditing) return <UserEditComposerWeb />;
  if (role === 'user') return <UserMessage />;
  return <AssistantMessage />;
};

// ---------------------------------------------------------------------------
// Thread Footer Composer
// ---------------------------------------------------------------------------
const FooterComposer: FC = () => {
  const { t } = useTranslation('common');
  return (
    <div className="tabbio-web-composer-footer pb-4">
      <WebComposer />
      <p className="tabbio-composer-disclaimer">{t('chatInputDisclaimer')}</p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Thread (main export)
// ---------------------------------------------------------------------------
export function Thread({
  initialPrompt,
  initialPromptId,
}: {
  initialPrompt?: string;
  initialPromptId?: string;
}) {
  const aui = useAui();

  // On iOS mobile web the keyboard overlays the layout viewport, hiding the
  // sticky composer; shrink the thread to the visible area so it stays above it.
  const keyboardInset = useWebKeyboardInset();

  // A quick action from the canvas (Shorter, Suggest edits, Fix with Tabbio)
  // is sent in this chat, beside the panel, with its selection as context.
  const threadRunning = useAuiState((state) => state.thread.isRunning);
  useArtifactQuickActions(null, (text) => aui.thread().append(text), !threadRunning);

  // Guarded outside React: a mount-scoped ref let Back-from-the-editor resend
  // the same prompt to the agent (issue #268).
  useEffect(() => {
    const prompt = normalizeInitialPrompt(initialPrompt);
    const scope = { promptId: initialPromptId };
    if (!shouldSendInitialPrompt(prompt, scope)) {
      return;
    }

    markInitialPromptSent(prompt!, scope);
    aui.thread().append(prompt!);
  }, [aui, initialPrompt, initialPromptId]);

  return (
    <ThreadPrimitive.Root
      className="tabbio-web-thread flex h-full min-h-0 flex-col overflow-hidden"
      style={{
        background: 'var(--bg)',
        ...(keyboardInset > 0
          ? { marginBottom: keyboardInset }
          : null),
      }}
    >
      {/* Keyed on the message count, not `isEmpty`: assistant-ui reports a
          thread with no messages as non-empty while its runtime `isLoading`,
          which on every load swapped the new-chat hero for an empty
          conversation for a frame. An existing thread never sits at zero
          messages here (ChatProvider.web holds its spinner until the history
          has loaded, and the runtime starts from it). */}
      <AuiIf condition={(s) => s.thread.messages.length === 0}>
        <EmptyState />
      </AuiIf>

      <AuiIf condition={(s) => s.thread.messages.length > 0}>
        <ThreadPrimitive.Viewport
          className="tabbio-web-messages flex min-h-0 flex-1 flex-col overflow-y-auto scroll-smooth"
          style={{ background: 'var(--bg)' }}
        >
          <ThreadPrimitive.Messages>
            {() => <WebMessage />}
          </ThreadPrimitive.Messages>

          <div
            aria-hidden
            className="shrink-0"
            style={{ minHeight: 24 }}
          />

          <ThreadPrimitive.ViewportFooter
            className="tabbio-thread-footer sticky bottom-0 z-10 mt-auto pt-6"
            style={{
              background: 'linear-gradient(to top, var(--bg) 60%, transparent)',
            }}
          >
            <ScrollToBottom />
            <FooterComposer />
          </ThreadPrimitive.ViewportFooter>
        </ThreadPrimitive.Viewport>
      </AuiIf>
    </ThreadPrimitive.Root>
  );
}
